import type {
  MeResponse,
  OfferView,
  RewardsResponse,
  WalletActivityItem,
  WalletResponse,
} from '@xchange/shared';
import { useCallback, useEffect, useState } from 'react';
import {
  api,
  RequestError,
  type ActivityItem,
  type AdminWalletPerson,
  type AdminWallets,
  type PairView,
} from '../api.js';
import type { Platform } from '../platform/index.js';
import { toast } from '../toast.js';
import { pollWhileVisible } from '../poll.js';
import { track } from '../track.js';
import { useWelcomeWatch } from '../welcomeWatch.js';
import { SharedLine, useSharedLine } from '../sharedLine.js';
import type { UsageReport } from '../api.js';
import {
  createWallet,
  loadSealed,
  markConfirmed,
  needsConfirmation,
  phraseOpens,
  withWallet,
} from '../wallet.js';
import { BUY_USDC_WELCOME_LINE } from './BuyUsdc.js';
import { Logo } from './Logo.js';
import { ForgetWallet } from './ForgetWallet.js';
import { Loading } from './Loading.js';
import { activeWalletId, listWallets } from '../wallet.js';
import { walletWorth } from '../worth.js';
import { PinPad } from './PinPad.js';
import { RestoreWords, WordsCheck } from './WordsCheck.js';
import { ago, Mark, Market, perCoin } from './Market.js';
import { Balance } from './Balance.js';
import { Earn } from './Earn.js';
import { amountText, assetName, xgeText, type Asset } from '../assets.js';
import { inviteText, referralLine, shareTargets } from '../referral.js';
import { CURRENCIES, aboutLocal, currencyName } from '../localMoney.js';
import { useLocalMoney } from '../money.js';

type Step =
  | 'loading'
  | 'welcome'
  | 'restore'
  | 'pin'
  | 'confirm'
  | 'creating'
  | 'home'
  | 'settings'
  | 'phrase-pin'
  | 'phrase'
  | 'phrase-check'
  | 'forget'
  | 'admin'
  | 'admin-person';
type Tab = 'home' | 'trade' | 'earn' | 'balance';

/**
 * The self-custody product, for people who come in through @xchangehq_bot.
 *
 * Every screen fits the viewport. Buy and Sell are the product; the wallet is
 * invisible until it matters, and the words "private key" never appear.
 */
export function SelfCustody({
  platform,
  me,
  freshPhrase = null,
  onMe,
  onReady,
  onTerms,
}: {
  platform: Platform;
  me: MeResponse;
  /** A wallet made moments ago in a browser: its words, to show and type back first. */
  freshPhrase?: string | null;
  /** The account changed (a name chosen). */
  onMe?: (me: MeResponse) => void;
  /** A new wallet's words have been typed back: it is ready to use. */
  onReady?: () => void;
  onTerms: () => void;
}) {
  const [step, setStep] = useState<Step>('loading');
  const [firstPin, setFirstPin] = useState<string | null>(null);
  const [restorePhrase, setRestorePhrase] = useState('');
  const [wallet, setWallet] = useState<WalletResponse | null>(null);
  const [pairs, setPairs] = useState<PairView[]>([]);
  const [tab, setTab] = useState<Tab>('home');
  /** A bid from Earn for the Swap tab to open with; a new key for each press. */
  const [bidPreset, setBidPreset] = useState<{
    key: number;
    pair: string;
    price: bigint | null;
    amount: bigint | null;
  } | null>(null);
  const [open, setOpen] = useState<OfferView[]>([]);
  // Set while no Gajumaru node answers: Gaju pairs say their orders wait.
  const [gajuWaits, setGajuWaits] = useState(false);
  const [rewards, setRewards] = useState<RewardsResponse | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [admin, setAdmin] = useState<AdminWallets | null>(null);
  const [usage, setUsage] = useState<UsageReport | null>(null);
  const [person, setPerson] = useState<{
    who: AdminWalletPerson;
    items: (WalletActivityItem & { asset: Asset })[] | null;
  } | null>(null);
  const openAdmin = (): void => {
    setAdmin(null);
    setStep('admin');
    void api
      .adminUsage(7)
      .then(setUsage)
      .catch(() => {
        setUsage(null);
      });
    void api
      .adminWallets()
      .then(setAdmin)
      .catch((e: unknown) => {
        toast.err(e instanceof Error ? e.message : 'Could not load.');
        setStep('home');
      });
  };
  const [mine, setMine] = useState<OfferView[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [phrase, setPhrase] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A new wallet's phrase is shown and typed back before anything else; this
  // is true from its creation until the words have been typed.
  const [mustConfirm, setMustConfirm] = useState(false);
  const [nameDraft, setNameDraft] = useState(me.displayName);
  const [nameBusy, setNameBusy] = useState(false);
  const [checkOk, setCheckOk] = useState(false);
  // The chosen currency and the day's rates, held once here: the trading
  // screen reads its figures through it, Settings picks it.
  const money = useLocalMoney();

  const load = useCallback(async () => {
    const [w, p, r] = await Promise.all([
      api.wallet(),
      api.pairs().catch(() => [] as PairView[]),
      api.rewards().catch(() => null),
    ]);
    setWallet(w);
    setPairs(p);
    setRewards(r);
    return w;
  }, []);

  useEffect(() => {
    void (async () => {
      const sealed = await loadSealed(platform).catch(() => null);
      await load();
      // Made moments ago in the browser: its words come first.
      if (sealed !== null && freshPhrase !== null) {
        setPhrase(freshPhrase);
        setMustConfirm(true);
        setStep('phrase');
        return;
      }
      // A wallet made here whose words were never typed back: back to them,
      // behind the PIN, before anything else.
      if (sealed !== null && needsConfirmation()) {
        setMustConfirm(true);
        setStep('phrase-pin');
        return;
      }
      // Nothing opens without a wallet on this phone. The server knowing an
      // address is not enough: after a Telegram reinstall it knows the
      // address and the phone holds nothing. Then it is the start again:
      // a new wallet, or the words of the old one.
      setStep(sealed !== null ? 'home' : 'welcome');
    })().catch((e: unknown) => {
      setError(e instanceof Error ? e.message : 'Could not open your wallet.');
    });
  }, [platform, load, freshPhrase]);

  const loadMarket = useCallback(async () => {
    const [r, a] = await Promise.all([
      api.offers(),
      api.activity().catch(() => [] as ActivityItem[]),
    ]);
    setOpen(r.open);
    setMine(r.mine);
    setGajuWaits((r.gajumaru ?? 'ok') !== 'ok');
    setActivity(a);
  }, []);
  useEffect(() => {
    if (step !== 'home') return;
    void loadMarket().catch(() => undefined);
    return pollWhileVisible(() => {
      void load().catch(() => undefined);
      void loadMarket().catch(() => undefined);
    }, 10_000);
  }, [step, load, loadMarket]);

  const create = async (pin: string): Promise<void> => {
    setStep('creating');
    try {
      // Restoring onto an account that already has a wallet: the phrase must
      // open that wallet, not another. The server would accept a new address
      // and the money would stay at the old one.
      if (
        restorePhrase !== '' &&
        wallet?.addresses.solana !== undefined &&
        wallet.addresses.solana !== null &&
        !phraseOpens(restorePhrase, wallet.addresses.solana)
      ) {
        toast.err('That phrase opens a different wallet, not the one this account uses.');
        setStep('restore');
        return;
      }
      const made = await createWallet(
        platform,
        me.telegramId,
        pin,
        restorePhrase === '' ? undefined : restorePhrase,
      );
      setRestorePhrase('');
      track(made.phrase === null ? 'restored' : 'pin_set');
      await load();
      if (made.phrase === null) {
        // Restored from words the person already has.
        toast.ok('Your wallet is ready');
        setStep('home');
      } else {
        // New: the words, then the words typed back, then ready.
        setPhrase(made.phrase);
        setMustConfirm(true);
        setStep('phrase');
      }
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      // Nothing about this reaches the server otherwise: say what failed here,
      // and tell the server too, so a pattern across phones can be seen.
      void api.reportClientError('create-wallet', reason).catch(() => undefined);
      setError(
        e instanceof RequestError
          ? e.message
          : `Could not create the wallet: ${reason}. Try again; if it happens again, update Telegram.`,
      );
      setStep('welcome');
    }
  };

  if (error !== null) {
    return (
      <div className="screen center">
        <h2 className="title">Something went wrong</h2>
        <p className="hint">{error}</p>
        <button
          className="primary"
          onClick={() => {
            setError(null);
            setStep('welcome');
          }}
        >
          Try again
        </button>
      </div>
    );
  }
  if (step === 'loading' || step === 'creating') {
    return (
      <div className="screen center">
        <Logo height={44} />
        <p className="hint">
          {step === 'creating' ? 'Making your wallet. A few seconds.' : 'One moment.'}
        </p>
      </div>
    );
  }
  if (step === 'welcome') {
    return (
      <PhoneWelcome
        where={platform.kind === 'web' ? 'web' : 'telegram'}
        onStart={() => {
          track('wallet_start', { from: platform.kind === 'web' ? 'welcome' : 'telegram' });
          setStep('pin');
        }}
        onRestore={() => {
          track('restore_start');
          setStep('restore');
        }}
      />
    );
  }
  if (step === 'restore') {
    return (
      <div className="screen center">
        <h2 className="title">Your recovery phrase</h2>
        <p className="hint">Your twelve words, in order, one in each box.</p>
        <RestoreWords
          initial={restorePhrase}
          onPhrase={(p) => {
            setRestorePhrase(p);
            setStep('pin');
          }}
        />
        <button
          className="ghost"
          onClick={() => {
            setRestorePhrase('');
            setStep('welcome');
          }}
        >
          Back
        </button>
      </div>
    );
  }
  if (step === 'pin') {
    return (
      <PinPad
        title="Choose a PIN"
        hint="Six digits. It protects your wallet on this phone."
        onPin={(pin) => {
          track('pin_first', { fresh: restorePhrase === '' });
          setFirstPin(pin);
          setStep('confirm');
        }}
      />
    );
  }
  if (step === 'confirm') {
    return (
      <PinPad
        title="Type it again"
        onPin={(pin) => {
          if (pin === firstPin) {
            setFirstPin(null);
            void create(pin);
          } else {
            track('pin_mismatch');
            toast.err('The PINs did not match. Start again.');
            setFirstPin(null);
            setStep('pin');
          }
        }}
      />
    );
  }
  if (step === 'phrase-pin') {
    return (
      <PinPad
        title="Your PIN"
        hint={
          mustConfirm
            ? 'Your recovery phrase has not been written down yet. Enter your PIN to see it.'
            : 'To show your recovery phrase.'
        }
        onPin={(pin) => {
          void withWallet(platform, pin, (w) => w.phrase)
            .then((p) => {
              setPhrase(p);
              setStep('phrase');
            })
            .catch((e: unknown) => {
              // Only a failed decrypt is a wrong PIN. With no wallet on the
              // phone (its storage cleared under a running app), recover.
              const reason = e instanceof Error ? e.message : String(e);
              if (reason === 'No wallet on this device') {
                toast.err('This phone has no copy of your wallet.');
                setMustConfirm(false);
                setStep('welcome');
                return;
              }
              toast.err(reason === 'Wrong PIN' ? reason : `Could not open the wallet: ${reason}`);
              // The words come before anything else: stay until the PIN is right.
              if (!mustConfirm) setStep('settings');
            });
        }}
      />
    );
  }
  if (step === 'phrase') {
    return (
      <div className="screen center">
        {mustConfirm && platform.kind === 'web' && <p className="pin-step">Step 2 of 3</p>}
        <h2 className="title">{mustConfirm ? 'Your twelve words' : 'Your recovery phrase'}</h2>
        <p className="hint">
          {mustConfirm
            ? 'Write them on paper, in order. They are the only way back in. Never show them to anyone.'
            : 'These twelve words open your wallet on any phone. Keep them safe and private.'}
        </p>
        {/* Numbered whole words, never broken across a line, so they are copied down right. */}
        <ol className="card phrase-words" data-testid="phrase">
          {(phrase ?? '').split(/\s+/).map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ol>
        <button
          className="primary"
          onClick={() => {
            if (mustConfirm) {
              setCheckOk(false);
              setStep('phrase-check');
            } else {
              setPhrase(null);
              setStep('settings');
            }
          }}
          data-testid="phrase-done"
        >
          {mustConfirm ? "I've written them down" : 'Done'}
        </button>
      </div>
    );
  }
  if (step === 'phrase-check') {
    return (
      <div className="screen center">
        {platform.kind === 'web' && <p className="pin-step">Step 3 of 3</p>}
        <h2 className="title">Type your twelve words</h2>
        <p className="hint">From what you wrote down, one word in each box.</p>
        {phrase !== null && <WordsCheck phrase={phrase} onMatch={setCheckOk} />}
        <button
          className="primary"
          disabled={!checkOk}
          onClick={() => {
            markConfirmed();
            track('phrase_confirmed');
            setPhrase(null);
            setCheckOk(false);
            setMustConfirm(false);
            toast.ok('Your wallet is ready');
            setStep('home');
            onReady?.();
          }}
          data-testid="check-done"
        >
          Continue
        </button>
        <button
          className="ghost"
          onClick={() => {
            setCheckOk(false);
            setStep('phrase');
          }}
        >
          Show the words again
        </button>
      </div>
    );
  }
  if (step === 'forget') {
    // Telegram keeps one wallet: the open one, behind its PIN.
    const id = activeWalletId();
    const entry = listWallets().find((w) => w.id === id);
    if (entry === undefined) return <Loading text="One moment." />;
    const worth = wallet === null ? null : walletWorth(wallet.balances, pairs);
    const orders = mine.filter(
      (o) => o.mine === 'maker' && !['done', 'cancelled', 'expired', 'failed'].includes(o.status),
    ).length;
    return (
      <div className="screen">
        <ForgetWallet
          platform={platform}
          entry={entry}
          isOpen
          address={wallet?.addresses.solana ?? entry.solana}
          seen={
            wallet === null
              ? null
              : {
                  gaju: wallet.balances.GAJU,
                  usdc: wallet.balances.USDC,
                  xge: wallet.balances.XGE,
                  worth: worth === null ? null : worth.toString(),
                  orders,
                  at: new Date().toISOString(),
                }
          }
          live
          onShowWords={() => {
            setStep('phrase-pin');
          }}
          onKeep={() => {
            setStep('settings');
          }}
          onRemoved={() => {
            // Off this phone and out of Telegram's cloud: the start again,
            // where the twelve words bring it back.
            setWallet(null);
            setStep('welcome');
          }}
        />
      </div>
    );
  }
  if (step === 'settings') {
    return (
      <div className="screen">
        <div className="header">
          <button
            className="ghost"
            onClick={() => {
              setStep('home');
            }}
          >
            ‹ Back
          </button>
          <h2 className="title">Settings</h2>
          <span style={{ width: 56 }} />
        </div>
        <div className="card" data-testid="name-card">
          <div className="label">Your name</div>
          <div className="sub">
            Shown on Earn's board when your bid counts; nobody sees who is behind a trade.
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const name = nameDraft.trim();
              if (name === '' || name === me.displayName) return;
              setNameBusy(true);
              api
                .updateMe({ displayName: name })
                .then((m) => {
                  onMe?.(m);
                  toast.ok('Saved');
                })
                .catch((err: unknown) => {
                  toast.err(err instanceof Error ? err.message : 'Could not save the name.');
                })
                .finally(() => {
                  setNameBusy(false);
                });
            }}
            style={{ display: 'flex', gap: 8, marginTop: 10 }}
          >
            <input
              className="field"
              value={nameDraft}
              maxLength={32}
              onChange={(e) => {
                setNameDraft(e.target.value);
              }}
              style={{ flex: 1, font: 'inherit', padding: 10 }}
              data-testid="name-input"
            />
            <button
              className="primary"
              type="submit"
              disabled={nameBusy || nameDraft.trim() === '' || nameDraft.trim() === me.displayName}
              data-testid="name-save"
            >
              Save
            </button>
          </form>
        </div>
        <div className="card" data-testid="currency-card">
          <div className="label">Figures in your money</div>
          <div className="sub">
            Everything you only read is shown in this currency too. What you type or sign stays in
            USDC.
          </div>
          <select
            className="pick"
            value={money.currency ?? 'off'}
            onChange={(e) => {
              money.choose(e.target.value);
            }}
            aria-label="Figures in your money"
            data-testid="local-currency"
          >
            <option value="off">Dollars only</option>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {currencyName(c)} ({c})
              </option>
            ))}
          </select>
          <p className="small" style={{ marginTop: 8 }}>
            A rough figure from the European Central Bank's daily rates.
            {(() => {
              const held = BigInt(wallet?.balances.USDC ?? '0');
              const line = aboutLocal(held > 0n ? held : 1_000_000n, money.currency, money.rates);
              return line === null ? null : (
                <>
                  {' '}
                  {held > 0n ? 'Your USDC: ' : '1 USDC: '}
                  <span data-testid="local-currency-preview">{line}</span>.
                </>
              );
            })()}
          </p>
        </div>
        <button
          className="secondary"
          onClick={() => {
            setStep('phrase-pin');
          }}
          data-testid="show-phrase"
        >
          Show recovery phrase
        </button>
        <button
          className="secondary"
          onClick={() => {
            setStep('forget');
          }}
          data-testid="forget-wallet"
        >
          Remove from this device
        </button>
        <button className="secondary" disabled>
          Connect an external wallet (soon)
        </button>
        <button className="secondary" onClick={onTerms}>
          Terms of use
        </button>
        {rewards !== null && (
          <div className="card" data-testid="rewards">
            <div className="label">Your XGE rewards</div>
            <div className="big">{xgeText(rewards.inWallet)} XGE</div>
            <div className="sub">
              {BigInt(rewards.accrued) > 0n
                ? `${xgeText(rewards.accrued)} arriving with the next daily payout`
                : `${xgeText(rewards.todayAllowance)} XGE shared among everyone who swaps today`}
            </div>
            <p className="small" style={{ marginTop: 10 }}>
              {referralLine(rewards)}
            </p>
            <button
              className="primary"
              style={{ marginTop: 10 }}
              onClick={() => {
                setShareOpen(true);
              }}
              data-testid="share-link"
            >
              Share my link
            </button>
          </div>
        )}
        {shareOpen && rewards !== null && (
          <div
            className="sheet-backdrop"
            onClick={() => {
              setShareOpen(false);
            }}
          >
            <div
              className="sheet"
              onClick={(e) => {
                e.stopPropagation();
              }}
              data-testid="share-sheet"
            >
              <div className="grip" />
              <div className="label">Share your link</div>
              <p className="small" style={{ wordBreak: 'break-all', userSelect: 'all' }}>
                {rewards.refLink}
              </p>
              <button
                className="primary"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(rewards.refLink)
                    .then(() => {
                      toast.ok('Link copied. Paste it anywhere.');
                      setShareOpen(false);
                    })
                    .catch(() => {
                      toast.err('Could not copy. Long-press the link above.');
                    });
                }}
                data-testid="copy-link"
              >
                Copy link
              </button>
              <div className="share-row">
                {shareTargets(rewards.refLink, inviteText(rewards)).map(([name, url]) => (
                  <button
                    className="secondary"
                    key={name}
                    onClick={() => {
                      platform.openLink(url);
                      setShareOpen(false);
                    }}
                    data-testid={`share-${name.toLowerCase()}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        <p className="small" style={{ textAlign: 'center' }}>
          XGE is xchange's reward token. It is not an investment, and xchange never sets its price.
        </p>
      </div>
    );
  }

  if (step === 'admin') {
    const short = (a: string | null): string =>
      a === null ? '—' : `${a.slice(0, 4)}…${a.slice(-4)}`;
    return (
      <div className="screen">
        <div className="header">
          <button
            className="ghost"
            onClick={() => {
              setStep('home');
            }}
          >
            ‹ Back
          </button>
          <h2 className="title">People</h2>
          <span style={{ width: 56 }} />
        </div>
        {admin === null ? (
          <p className="hint">Loading.</p>
        ) : (
          <>
            <div className="cards three">
              <div className="card tile">
                <div className="label">Wallets</div>
                <div className="big">{admin.total}</div>
                <div className="sub">{admin.activeToday} today</div>
              </div>
              <div className="card tile">
                <div className="label">Swapped</div>
                <div className="big">{admin.haveTraded}</div>
                <div className="sub">{admin.trades} trades</div>
              </div>
              <div className="card tile">
                <div className="label">XGE paid</div>
                <div className="big">{xgeText(admin.rewardsPaid)}</div>
                <div className="sub">rewards</div>
              </div>
            </div>
            {usage !== null && (
              <div className="card" data-testid="admin-usage">
                <div className="label">
                  Last 7 days: {usage.devices} devices, {usage.returning} came back
                </div>
                {usage.funnel.map((f) => (
                  <div className="row" key={f.step} style={{ padding: '6px 0' }}>
                    <span className="sub" style={{ flex: 1 }}>
                      {f.step}
                    </span>
                    <strong>{f.devices}</strong>
                  </div>
                ))}
                {usage.errors.length > 0 && (
                  <p className="small" style={{ marginTop: 8 }}>
                    Went wrong at: {usage.errors.map((e) => `${e.where} (${e.count})`).join(', ')}
                  </p>
                )}
              </div>
            )}
            <div className="card">
              <div className="label">In their wallets, as last seen</div>
              <div className="sub" style={{ color: 'var(--text)' }}>
                {amountText('GAJU', BigInt(admin.holdings.gaju))} ·{' '}
                {amountText('USDC', BigInt(admin.holdings.usdc))} ·{' '}
                {amountText('XGE', BigInt(admin.holdings.xge))}
              </div>
            </div>
            <div className="card" style={{ padding: '0 var(--pad)' }}>
              <div className="list" data-testid="admin-people">
                {admin.people.map((p) => (
                  <button
                    className="row"
                    key={p.id}
                    onClick={() => {
                      setPerson({ who: p, items: null });
                      setStep('admin-person');
                      void api
                        .adminWalletActivity(p.id)
                        .then((items) => {
                          setPerson((cur) => (cur?.who.id === p.id ? { who: p, items } : cur));
                        })
                        .catch(() => {
                          setPerson((cur) => (cur?.who.id === p.id ? { who: p, items: [] } : cur));
                        });
                    }}
                  >
                    <div>
                      <div className="desc">
                        {p.name}
                        {p.username === null ? '' : ` @${p.username}`}
                      </div>
                      <div className="meta">
                        {p.trades} {p.trades === 1 ? 'trade' : 'trades'} · {xgeText(p.xgeRewards)}{' '}
                        XGE earned · joined {ago(p.joinedAt)}
                      </div>
                      <div className="meta">
                        {short(p.addresses.solana)} · {short(p.addresses.gajumaru)}
                      </div>
                    </div>
                    <div className="amt" style={{ fontSize: 13, fontWeight: 400 }}>
                      {amountText('GAJU', BigInt(p.gaju))}
                      <br />
                      {amountText('USDC', BigInt(p.usdc))}
                      <br />
                      {amountText('XGE', BigInt(p.xge))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  if (step === 'admin-person' && person !== null) {
    const p = person.who;
    return (
      <div className="screen">
        <div className="header">
          <button
            className="ghost"
            onClick={() => {
              setStep('admin');
            }}
          >
            ‹ People
          </button>
          <h2 className="title">{p.name}</h2>
          <span style={{ width: 56 }} />
        </div>
        <div className="card">
          <div className="label">
            {p.username === null ? '' : `@${p.username} · `}joined {ago(p.joinedAt)}
          </div>
          <div className="sub" style={{ color: 'var(--text)', marginTop: 6 }}>
            {amountText('GAJU', BigInt(p.gaju))} · {amountText('USDC', BigInt(p.usdc))} ·{' '}
            {amountText('XGE', BigInt(p.xge))}
          </div>
          <div className="small" style={{ marginTop: 6, wordBreak: 'break-all' }}>
            {p.addresses.solana ?? '—'}
            <br />
            {p.addresses.gajumaru ?? '—'}
          </div>
        </div>
        {person.items === null ? (
          <p className="hint">Loading.</p>
        ) : person.items.length === 0 ? (
          <div className="empty">Nothing yet.</div>
        ) : (
          <div className="card" style={{ padding: '0 var(--pad)' }}>
            <div className="list">
              {person.items.map((it) => (
                <button
                  className="row"
                  key={`${it.asset}:${it.id}`}
                  onClick={() => {
                    if (it.url !== null) platform.openLink(it.url);
                  }}
                >
                  <div>
                    <div className="desc">{it.text}</div>
                    <div className="meta">
                      {new Date(it.at).toLocaleString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {it.url !== null ? ' · on chain' : ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'home', label: 'Home', icon: '⌂' },
    { id: 'trade', label: 'Swap', icon: '⇄' },
    { id: 'earn', label: 'Earn', icon: '✦' },
    { id: 'balance', label: 'Balance', icon: '◎' },
  ];
  return (
    <div className="shell">
      <div className="shell-top">
        <div className="brand">
          <Logo height={22} />
          <h2 className="title">xchange</h2>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {me.isAdmin && (
            <button className="icon" aria-label="People" onClick={openAdmin} data-testid="admin">
              ◉
            </button>
          )}
          <button
            className="icon"
            aria-label="Settings"
            onClick={() => {
              setStep('settings');
            }}
            data-testid="settings"
          >
            ≡
          </button>
        </div>
      </div>
      <div className="shell-body">
        {tab === 'home' && (
          <Home
            pairs={pairs}
            activity={activity}
            open={open}
            rewards={rewards}
            onTrade={() => {
              setTab('trade');
            }}
          />
        )}
        {tab === 'trade' && (
          <Market
            platform={platform}
            pairs={pairs}
            open={open}
            mine={mine}
            balances={wallet?.balances ?? null}
            solanaAddress={wallet?.addresses.solana ?? null}
            money={money}
            reload={loadMarket}
            gajuWaits={gajuWaits}
            rewards={rewards}
            activity={activity}
            preset={bidPreset}
          />
        )}
        {tab === 'earn' && (
          <div className="tab-screen">
            <Earn
              pairs={pairs}
              wordsDone={!needsConfirmation()}
              funded={
                wallet !== null &&
                Object.values(wallet.balances).some((b) => b !== null && BigInt(b) > 0n)
              }
              onBid={(preset, pair) => {
                const name = preset?.pair ?? pair;
                if (name !== null)
                  setBidPreset({
                    key: Date.now(),
                    pair: name,
                    price: preset?.price ?? null,
                    amount: preset?.amount ?? null,
                  });
                setTab('trade');
                if (preset !== null) toast.ok('A bid 10% under the best offer is filled in.');
              }}
            />
          </div>
        )}
        {tab === 'balance' && (
          <Balance
            platform={platform}
            wallet={wallet}
            reload={async () => {
              await load();
            }}
          />
        )}
      </div>
      <nav className="tabbar" aria-label="Sections">
        {tabs.map((t) => (
          <button
            key={t.id}
            aria-pressed={tab === t.id}
            onClick={() => {
              setTab(t.id);
            }}
            data-testid={`tab-${t.id}`}
          >
            <span className="tab-icon" aria-hidden="true">
              {t.icon}
            </span>
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

/** The Home tab: what is happening on xchange right now, with nobody named. */
/** $1.2K, $130.1M: dollars at a glance, from USDC smallest units. */
function compactUsd(minor: bigint): string {
  const dollars = Number(minor / 10_000n) / 100;
  const units: [number, string][] = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ];
  for (const [n, suffix] of units) {
    if (dollars < n) continue;
    const v = dollars / n;
    // Three significant figures or so: $1.23M, $12.3M, $123M.
    const text = v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2);
    return `$${text}${suffix}`;
  }
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Home({
  pairs,
  activity,
  open,
  rewards,
  onTrade,
}: {
  pairs: PairView[];
  activity: ActivityItem[];
  open: OfferView[];
  rewards: RewardsResponse | null;
  onTrade: () => void;
}) {
  const pairOf = (name: string): PairView | undefined => pairs.find((p) => p.name === name);
  const line = (a: ActivityItem): string => {
    const pair = pairOf(a.pair);
    if (pair === undefined) return '';
    const base = amountText(pair.base as Asset, BigInt(a.baseAmount));
    const quote = amountText(pair.quote as Asset, BigInt(a.quoteAmount));
    if (a.kind === 'trade')
      return a.side === 'sell' ? `${base} sold for ${quote}` : `${base} bought for ${quote}`;
    return a.side === 'sell' ? `${base} on offer for ${quote}` : `${quote} bid for ${base}`;
  };
  const trades = activity.filter((a) => a.kind === 'trade');
  const bidsOpen = open.filter((o) => o.side === 'buy').length;
  // Each coin's market cap: its supply on chain at the last trade against
  // USDC. It moves the moment a trade settles, since pairs reload with the
  // rest of the home screen.
  const caps = (['GAJU/USDC', 'XGE/USDC'] as const)
    .map(pairOf)
    .filter((p): p is PairView => p !== undefined);
  const capOf = (p: PairView): bigint | null =>
    p.baseSupply === null || p.lastPrice === null
      ? null
      : (BigInt(p.baseSupply) * BigInt(p.lastPrice)) / BigInt(p.priceUnit);
  return (
    <div className="tab-screen">
      <div className="hero">
        <div className="hero-title">Where communities meet liquidity</div>
        <div className="hero-sub">
          {open.length === 0 && trades.length === 0
            ? 'Quiet right now. Be the first to place a bid or an offer.'
            : `${bidsOpen} ${bidsOpen === 1 ? 'bid' : 'bids'} · ${open.length - bidsOpen} ${open.length - bidsOpen === 1 ? 'offer' : 'offers'} · ${trades.length} recent ${trades.length === 1 ? 'trade' : 'trades'}`}
        </div>
      </div>
      {caps.length > 0 && (
        <div className="cards caps" data-testid="caps">
          {caps.map((p) => {
            const cap = capOf(p);
            return (
              <div className="card cap" key={p.name}>
                <div className="cap-head">
                  <Mark asset={p.base} size={18} />
                  <span className="label">{assetName[p.base as Asset]} market cap</span>
                </div>
                <div className="big">{cap === null ? '—' : compactUsd(cap)}</div>
                <div className="sub">
                  {p.lastPrice === null ? (
                    'no trade yet'
                  ) : (
                    <>
                      {perCoin(p, p.lastPrice)} per {assetName[p.base as Asset]}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="card live">
        <div className="live-head">
          <span className="live-dot" aria-hidden="true" />
          <span className="label">Live</span>
          <span className="live-count">
            {activity.length === 0 ? '' : `${Math.min(activity.length, 30)} latest`}
          </span>
        </div>
        {activity.length === 0 ? (
          <div className="small" style={{ padding: '10px 0 14px' }}>
            Trades and offers appear here as they happen.
          </div>
        ) : (
          <div className="live-feed" data-testid="activity">
            {activity.slice(0, 30).map((a, i) => (
              <div className={`live-row${i === 0 ? ' newest' : ''}`} key={a.id}>
                <Mark asset={pairOf(a.pair)?.logo ?? 'GAJU'} size={20} />
                <div className="live-text">
                  <span className="live-line">{line(a)}</span>
                  <span className="live-meta">
                    {a.kind === 'trade' ? 'Settled' : 'Open'} · {ago(a.at)}
                  </span>
                </div>
                <span className={`live-side ${a.side}`}>{a.kind === 'trade' ? '●' : '○'}</span>
              </div>
            ))}
            <div className="live-fade" aria-hidden="true" />
          </div>
        )}
      </div>
      <button className="primary" onClick={onTrade} data-testid="trade-now">
        Swap now
      </button>
      {rewards !== null && rewards.active && (
        <p className="small" style={{ textAlign: 'center' }} data-testid="rewards-line">
          Today's pool: {xgeText(rewards.todayAllowance)} XGE, shared among everyone who swaps
          today.
        </p>
      )}
      <p className="small" style={{ textAlign: 'center' }}>
        Every trade settles through escrow on chain. xchange never holds your funds.
      </p>
    </div>
  );
}

/**
 * The first screen inside Telegram for someone with no wallet on this phone:
 * the same words as the browser's welcome, one button, and what the minute
 * holds. It notes the same anonymous events, so the two can be compared.
 */
function PhoneWelcome({
  where,
  onStart,
  onRestore,
}: {
  where: 'web' | 'telegram';
  onStart: () => void;
  onRestore: () => void;
}) {
  const watch = useWelcomeWatch(where);
  const line = useSharedLine();
  return (
    <div className="screen center" data-testid="phone-welcome" onPointerDown={watch.tapped}>
      <Logo height={44} />
      <h2 className="title">Start swapping now and get real rewards.</h2>
      <p className="hint">A PIN and twelve words. That's the whole sign-up.</p>
      <button className="primary" onClick={onStart} data-testid="create-wallet">
        Make my wallet
      </button>
      <p className="small">Your keys are made on this phone. xchange never has them.</p>
      {/* One line, no button: the card route is met properly inside the wallet. */}
      <p className="small" data-testid="welcome-buy-usdc">
        {BUY_USDC_WELCOME_LINE}
      </p>
      <SharedLine line={line} className="phone-shared" />
      <button className="ghost" onClick={onRestore}>
        I have a recovery phrase
      </button>
    </div>
  );
}
